import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  Account360,
  Contact,
  ContactsFilters,
  CreateContactPayload,
  UpdateContactExtendedPayload,
  UpdateContactPayload,
} from '../types';

interface ContactsResponse {
  contacts: Contact[];
  total?: number;
  limit?: number;
  offset?: number;
  stats?: {
    total: number;
    emailReachable: number;
    uniqueCompanies: number;
  };
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function useContacts(filters: ContactsFilters = {}) {
  return useQuery({
    queryKey: ['contacts', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      params.set('limit', String(filters.limit ?? 200));
      if (filters.offset) params.set('offset', String(filters.offset));
      if (filters.compact) params.set('compact', 'true');
      if (filters.totalOnly) params.set('stats', 'total');
      if (filters.sort) params.set('sort', filters.sort);
      if (filters.createdBy) params.set('createdBy', filters.createdBy);
      if (filters.segments) params.set('segments', filters.segments);
      const qs = params.toString();
      const { data } = await api.get<ContactsResponse>(`/api/contacts${qs ? `?${qs}` : ''}`);
      return {
        contacts: data.contacts ?? [],
        total: data.total ?? (data.contacts ?? []).length,
        limit: data.limit ?? filters.limit ?? 200,
        offset: data.offset ?? filters.offset ?? 0,
        stats: data.stats ?? {
          total: data.total ?? (data.contacts ?? []).length,
          emailReachable: (data.contacts ?? []).filter((contact) => contact.email).length,
          uniqueCompanies: new Set((data.contacts ?? []).map((contact) => contact.company).filter(Boolean)).size,
        },
      };
    },
    placeholderData: keepPreviousData,
  });
}

export function useContact(id: string | null | undefined) {
  return useQuery({
    queryKey: ['contacts', 'detail', id],
    queryFn: async (): Promise<Contact | null> => {
      if (!id) return null;
      try {
        const res = await api.get<any>(`/api/contacts/${id}`);
        const c = res.data?.contact || res.data;
        if (c && (c.id || c._id || c.name)) {
          return { ...c, id: c.id || c._id || id };
        }
      } catch {
        // Fallback to searching the full contacts directory if direct endpoint fails or server restart is pending
      }

      const { data } = await api.get<ContactsResponse>('/api/contacts');
      const found = (data?.contacts ?? []).find(
        (c: any) => c.id === id || c._id === id || String(c.id) === String(id) || String(c._id) === String(id)
      );
      if (found) {
        return { ...found, id: (found as any).id || (found as any)._id || id };
      }
      return null;
    },
    enabled: !!id,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useContactMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['contacts'] });

  const createContact = useMutation({
    mutationFn: async (payload: CreateContactPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/contacts', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateContact = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateContactPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/contacts/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteContact = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/contacts/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  // Additive BizPlus-parity + Account Master fields (client-approved audit —
  // "Contact as single source of truth"). Separate write path from the
  // frozen basic CRUD above — see the header comment in ../types.ts.
  const updateContactExtended = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateContactExtendedPayload }) => {
      const { data } = await api.patch<{ ok: boolean; contact: Contact }>(`/api/contacts-identity/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  /** Admin-only: close a reassignment request left pending from before reassignment was retired. */
  const decideReassignment = useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: 'approve' | 'reject' }) => {
      const { data } = await api.post<{ ok: boolean; contact: Contact; cascade: { pipelineRecords: number; orders: number; invoices: number } }>(
        `/api/contacts-identity/${id}/reassignment/${decision}`
      );
      return data;
    },
    onSuccess: invalidate,
  });

  return { createContact, updateContact, deleteContact, updateContactExtended, decideReassignment };
}

/** Account 360 dashboard — GET /api/contacts-identity/:id/360. */
export function useAccount360(contactId: string | null | undefined) {
  return useQuery({
    queryKey: ['contacts', 'account-360', contactId],
    queryFn: async () => {
      const { data } = await api.get<Account360>(`/api/contacts-identity/${contactId}/360`);
      return data;
    },
    enabled: !!contactId,
  });
}
